// ComponentSwitch — renders one of the 6 protocol component types per
// design-system §5. Chart.js integration (§5.3) is in `Chart.tsx`; everything
// else renders inline below.
//
// `onAction` + `pending` propagate from `Chat` down through
// `Bubble`. ConfirmRender fires `onAction(action_id)` on user tap of אישור;
// rows in a list with a non-empty `row.action_id` become buttons that fire
// the same callback. Both surfaces POST /chat with `{action_id}` so the
// server agent can dispatch the named action (typically `delete_event`).
// While `pending` is true (the parent has an action in flight), the
// destructive surfaces render disabled to prevent double-fire races.

import { useEffect, useState } from 'react';
import type { Component } from '../protocol.ts';
import { Chart } from './Chart.tsx';

type ActionCtx = {
  onAction?: (action_id: string) => void;
  pending?: boolean;
};

// ---- Card wrapper ---------------------------------------------------------

const Card = ({ children }: { children: React.ReactNode }) => (
  <div className="mt-2 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-3 text-sm shadow-[var(--shadow-bubble)]">
    {children}
  </div>
);

// ---- List (§5.1) — 4 variants ---------------------------------------------

type ListComp = Extract<Component, { type: 'list' }>;
type StatusColor = 'green' | 'yellow' | 'red';

const STATUS_PILL_BG: Record<StatusColor, string> = {
  green: 'bg-[var(--color-good)]',
  yellow: 'bg-[var(--color-warn)]',
  red: 'bg-[var(--color-bad)]',
};

const StatusPill = ({ color, text }: { color: StatusColor; text: string }) => (
  <span
    className={
      'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold text-white tabular-nums ' +
      STATUS_PILL_BG[color]
    }
  >
    {text}
  </span>
);

const ListRender = ({ c, ctx }: { c: ListComp; ctx: ActionCtx }) => {
  if (c.rows.length === 0) {
    // Empty list variant — dashed-circle glyph, no emoji.
    return (
      <Card>
        <div className="font-bold mb-2">{c.title}</div>
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <div
            className="h-10 w-10 rounded-full border-2 border-dashed border-[var(--color-muted)]"
            aria-hidden="true"
          />
          <div className="text-xs text-[var(--color-muted)]">אין מה להציג</div>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="font-bold mb-2">{c.title}</div>
      <ul className="divide-y divide-[var(--color-border)]">
        {c.rows.map((r) => {
          const hasAction = !!r.action || !!r.action_id;
          // Rows carrying an `action_id` become real buttons that
          // fire the parent's onAction callback. Older list variants without
          // action_id keep the existing read-only ‹ chevron affordance and
          // remain non-interactive (the chevron is decorative).
          const clickable = !!r.action_id && !!ctx.onAction;
          const disabled = clickable && !!ctx.pending;
          const Inner = (
            <>
              <div className="min-w-0 flex-1 text-start">
                <div className="truncate">{r.label}</div>
                {r.sublabel ? (
                  <div className="text-xs text-[var(--color-muted)]">{r.sublabel}</div>
                ) : null}
              </div>
              <div className="ml-2 flex items-center gap-2">
                {r.status_color && r.status ? (
                  <StatusPill color={r.status_color} text={r.status} />
                ) : r.status ? (
                  <span className="text-xs text-[var(--color-muted)] tabular-nums">
                    {r.status}
                  </span>
                ) : null}
                {hasAction ? (
                  <span className="text-[var(--color-muted)]" aria-hidden="true">
                    ‹
                  </span>
                ) : null}
              </div>
            </>
          );
          if (clickable) {
            return (
              <li key={r.id} className="py-0">
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => ctx.onAction!(r.action_id!)}
                  className={
                    'flex w-full items-center justify-between py-2 text-start ' +
                    (disabled
                      ? 'opacity-60 cursor-not-allowed'
                      : 'cursor-pointer hover:bg-[var(--color-card-hover,transparent)]')
                  }
                  aria-label={`${r.label}${r.sublabel ? ` — ${r.sublabel}` : ''}`}
                >
                  {Inner}
                </button>
              </li>
            );
          }
          return (
            <li key={r.id} className="flex items-center justify-between py-2">
              {Inner}
            </li>
          );
        })}
      </ul>
      {c.pagination ? (
        <div className="mt-2 flex items-center justify-between text-xs text-[var(--color-muted)]">
          {/* 4.2 — isolate the numeric run: two LTR number groups
              with a neutral `/` separator reorder in RTL flow. dir="ltr"
              keeps it `shown / total`. */}
          <span className="tabular-nums" dir="ltr">
            {c.pagination.offset + c.rows.length} / {c.pagination.total}
          </span>
          {c.pagination.offset + c.rows.length < c.pagination.total ? (
            <button
              type="button"
              className="font-bold text-[var(--color-primary)]"
            >
              עוד
            </button>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
};

// ---- Detail (§5.2) — with / without actions -------------------------------

type DetailComp = Extract<Component, { type: 'detail' }>;

export const formatTimestamp = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  return `${hh}:${mm} ${dd}/${mo}`;
};

// Heuristic: phone numbers and timezones need LTR isolation inside an RTL
// page. Detect by label keywords (Hebrew + English) or by value shape.
const needsLtrIsolate = (label: string, value: string): boolean => {
  const labelLower = label.toLowerCase();
  if (
    labelLower.includes('phone') ||
    labelLower.includes('טלפון') ||
    labelLower.includes('נייד') ||
    labelLower.includes('timezone') ||
    labelLower.includes('אזור זמן')
  )
    return true;
  if (/^\+?\d[\d\s\-()]{4,}$/.test(value.trim())) return true;
  return false;
};

const DetailRender = ({ c }: { c: DetailComp }) => {
  // Cap at 10 fields — overflow handled by AI follow-up question.
  const fields = c.fields.slice(0, 10);
  return (
    <Card>
      {c.image ? (
        // event-attached image. Backend has already resolved any
        // `event:<uuid>` token to a fresh signed GET URL; the SPA just
        // renders it. `object-cover` + capped height keeps the card tidy
        // on tall portrait photos. `loading="lazy"` since the assistant
        // bubble may scroll above the fold by the time the user reads it.
        <img
          src={c.image}
          alt=""
          loading="lazy"
          className="mb-2 max-h-64 w-full rounded-xl border border-[var(--color-border)] object-cover"
        />
      ) : null}
      <div className="font-bold">{c.title}</div>
      {c.subtitle ? (
        <div className="mb-2 text-xs text-[var(--color-muted)]">{c.subtitle}</div>
      ) : null}
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        {fields.map((f, i) => {
          const isolate = needsLtrIsolate(f.label, f.value);
          return (
            <div key={`${f.label}-${i}`} className="contents">
              <dt className="text-[var(--color-muted)]">{f.label}</dt>
              <dd className="break-words">
                {isolate ? (
                  <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>
                    {f.value}
                  </span>
                ) : (
                  f.value
                )}
              </dd>
            </div>
          );
        })}
      </dl>
      {c.timestamp ? (
        <div className="mt-2 text-xs text-[var(--color-muted)] tabular-nums">
          {formatTimestamp(c.timestamp)}
        </div>
      ) : null}
      {c.actions && c.actions.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {c.actions.map((a, i) => (
            <button
              key={`${a.label}-${i}`}
              type="button"
              className={
                'rounded-md px-3 py-1.5 text-sm font-bold ' +
                (a.style === 'danger'
                  ? 'bg-[var(--color-bad)] text-white'
                  : 'bg-[var(--color-primary)] text-white')
              }
            >
              {a.label}
            </button>
          ))}
        </div>
      ) : null}
    </Card>
  );
};

// ---- Chart (§5.3) — full Chart.js render delegated to Chart.tsx ----------

type ChartComp = Extract<Component, { type: 'chart' }>;

const ChartRender = ({ c }: { c: ChartComp }) => <Chart c={c} />;

// ---- Stat (§5.4) — 5 variants ---------------------------------------------

type StatComp = Extract<Component, { type: 'stat' }>;

const trendArrow = (trend?: 'up' | 'down' | 'flat'): string => {
  if (trend === 'up') return '↑';
  if (trend === 'down') return '↓';
  return '→';
};

// Resolve color from severity (explicit) or trend (implicit). Severity wins.
const statTrendColor = (
  severity?: 'neutral' | 'good' | 'warn' | 'bad',
  trend?: 'up' | 'down' | 'flat',
): string => {
  if (severity === 'good') return 'var(--color-good)';
  if (severity === 'warn') return 'var(--color-warn)';
  if (severity === 'bad') return 'var(--color-bad)';
  if (severity === 'neutral') return 'var(--color-muted)';
  // Implicit: up = good, down = bad, flat = muted (will read wrong for
  // negative-good cases — that's why severity exists). UI cannot infer
  // direction-goodness without it.
  if (trend === 'up') return 'var(--color-good)';
  if (trend === 'down') return 'var(--color-bad)';
  return 'var(--color-muted)';
};

const StatRender = ({ c }: { c: StatComp }) => {
  // Inline-pill variant when there's no trend and no context — a compact
  // chip that reads inside an AI sentence. Backend decides by omitting both.
  const isInlinePill = c.trend == null && !c.context && c.severity == null;

  if (isInlinePill) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-[var(--color-primary-light)] px-3 py-1 text-sm font-bold text-[var(--color-primary)] tabular-nums">
        {c.label}: {c.value}
        {c.unit ?? ''}
      </span>
    );
  }

  // Trend card variant. Card width ~56% of bubble.
  const color = statTrendColor(c.severity, c.trend);
  return (
    <div
      className="mt-2 inline-block w-[56%] rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-3 text-sm shadow-[var(--shadow-bubble)]"
    >
      <div className="text-xs text-[var(--color-muted)]">{c.label}</div>
      <div className="mt-1 flex items-baseline gap-1 tabular-nums">
        <span className="text-xl font-bold">{c.value}</span>
        {c.unit ? <span className="text-xs">{c.unit}</span> : null}
        {c.trend ? (
          <span className="ml-1 text-base" style={{ color }} aria-hidden="true">
            {trendArrow(c.trend)}
          </span>
        ) : null}
      </div>
      {c.trend_label ? (
        <div className="mt-0.5 text-xs tabular-nums" style={{ color }}>
          {c.trend_label}
        </div>
      ) : null}
      {c.context ? (
        <div className="mt-1 text-xs text-[var(--color-muted)]">{c.context}</div>
      ) : null}
    </div>
  );
};

// ---- Confirm (§5.5) — 4 variants -----------------------------------------

type ConfirmComp = Extract<Component, { type: 'confirm' }>;

type ConfirmTapState = 'idle' | 'confirmed' | 'cancelled' | 'expired';

const formatHHMM = (date: Date): string => {
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

const CONFIRM_TIMEOUT_MS = 5 * 60 * 1000;

const ConfirmRender = ({ c, ctx }: { c: ConfirmComp; ctx: ActionCtx }) => {
  const [state, setState] = useState<ConfirmTapState>('idle');
  const [tappedAt, setTappedAt] = useState<Date | null>(null);

  // Front-end-only 5-minute timeout — replaces buttons with `פג תוקף`.
  useEffect(() => {
    if (state !== 'idle') return;
    const id = window.setTimeout(() => setState('expired'), CONFIRM_TIMEOUT_MS);
    return () => window.clearTimeout(id);
  }, [state]);

  const tap = (next: 'confirmed' | 'cancelled') => {
    setState(next);
    setTappedAt(new Date());
    // Confirm taps fire the action round-trip; cancel stays local
    // (per plan rev 2 — cancel = local UX, no server hit).
    if (next === 'confirmed' && ctx.onAction) {
      ctx.onAction(c.action_id);
    }
  };
  const disabled = !!ctx.pending;

  const isDanger = c.style === 'danger';

  if (state === 'confirmed' || state === 'cancelled') {
    const word = state === 'confirmed' ? 'אישור' : 'ביטול';
    return (
      <Card>
        <div className="font-bold">{c.title}</div>
        <div className="text-sm">{c.message}</div>
        <div
          className="mt-3 text-xs text-[var(--color-muted)] tabular-nums opacity-85 transition-opacity duration-150"
          aria-live="polite"
        >
          {word} · {tappedAt ? formatHHMM(tappedAt) : ''}
        </div>
      </Card>
    );
  }

  if (state === 'expired') {
    return (
      <Card>
        <div className="font-bold">{c.title}</div>
        <div className="text-sm">{c.message}</div>
        <div className="mt-3 text-xs text-[var(--color-muted)]">
          פג תוקף — שאלו שוב
        </div>
      </Card>
    );
  }

  // idle — show buttons. cancel DOM-first → visually LEFT in RTL.
  return (
    <Card>
      <div className="font-bold">{c.title}</div>
      <div className="text-sm mb-3">{c.message}</div>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={() => tap('cancelled')}
          className={
            'flex-1 rounded-lg border border-[var(--color-border)] py-2 text-sm ' +
            (disabled ? 'opacity-60 cursor-not-allowed' : '')
          }
        >
          {c.cancel_label ?? 'ביטול'}
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => tap('confirmed')}
          className={
            'flex-1 rounded-lg py-2 text-sm font-bold text-white ' +
            (isDanger ? 'bg-[var(--color-bad)] ' : 'bg-[var(--color-primary)] ') +
            (disabled ? 'opacity-60 cursor-not-allowed' : '')
          }
        >
          {c.confirm_label ?? 'אישור'}
          {c.badge != null && c.badge > 0 ? (
            <span className="ml-1 rounded-full bg-white/25 px-1.5 py-0.5 text-xs tabular-nums">
              {c.badge}
            </span>
          ) : null}
        </button>
      </div>
    </Card>
  );
};

// ---- Result (§5.6) — 4 variants ------------------------------------------

type ResultComp = Extract<Component, { type: 'result' }>;

const ResultRender = ({ c }: { c: ResultComp }) => {
  const isError = c.outcome === 'error';
  const glyph = isError ? '✗' : '✓';
  const color = isError ? 'var(--color-bad)' : 'var(--color-good)';
  // Hebrew aria prefix is render-time, NOT a protocol field.
  const ariaPrefix = isError ? 'כישלון:' : 'הצלחה:';

  // Cap breakdown pills at 3; overflow folded into a `+N נוספים` summary pill.
  const breakdownPills =
    c.breakdown && c.breakdown.length > 0
      ? c.breakdown.length <= 3
        ? c.breakdown.map((b) => ({ label: b.label, value: b.value, summary: false }))
        : [
            ...c.breakdown.slice(0, 3).map((b) => ({
              label: b.label,
              value: b.value,
              summary: false,
            })),
            {
              label: `+${c.breakdown.length - 3} נוספים`,
              value: '',
              summary: true,
            },
          ]
      : [];

  return (
    <Card>
      <div className="flex items-baseline gap-2">
        <span style={{ color }} aria-hidden="true">
          {glyph}
        </span>
        <div className="font-bold">
          <span className="sr-only">{ariaPrefix} </span>
          {c.title}
        </div>
      </div>
      {c.message ? (
        <div className="mt-1 text-xs text-[var(--color-muted)]">{c.message}</div>
      ) : null}
      {breakdownPills.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {breakdownPills.map((p, i) => (
            <span
              key={`${p.label}-${i}`}
              className={
                'rounded-full px-2 py-0.5 text-xs tabular-nums ' +
                (p.summary
                  ? 'bg-[var(--color-surface)] text-[var(--color-muted)]'
                  : 'bg-[var(--color-primary-light)] text-[var(--color-primary)]')
              }
            >
              {p.summary ? p.label : `${p.label}: ${p.value}`}
            </span>
          ))}
        </div>
      ) : null}
      {c.timestamp ? (
        <div className="mt-2 text-xs text-[var(--color-muted)] tabular-nums">
          {formatTimestamp(c.timestamp)}
        </div>
      ) : null}
      {c.retry || c.action ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {c.retry ? (
            <button
              type="button"
              className="rounded-md bg-[var(--color-primary)] px-3 py-1.5 text-sm font-bold text-white"
            >
              {c.retry.label}
            </button>
          ) : null}
          {c.action ? (
            <button
              type="button"
              className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm font-bold text-[var(--color-primary)]"
            >
              {c.action.label}
            </button>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
};

// ---- Switch ---------------------------------------------------------------

export const ComponentSwitch = ({
  component,
  onAction,
  pending,
}: {
  component: Component;
  onAction?: (action_id: string) => void;
  pending?: boolean;
}) => {
  const ctx: ActionCtx = { onAction, pending };
  switch (component.type) {
    case 'list':
      return <ListRender c={component} ctx={ctx} />;
    case 'detail':
      return <DetailRender c={component} />;
    case 'chart':
      return <ChartRender c={component} />;
    case 'stat':
      return <StatRender c={component} />;
    case 'confirm':
      return <ConfirmRender c={component} ctx={ctx} />;
    case 'result':
      return <ResultRender c={component} />;
  }
};

